<template>
  <FileReport />

  <div class="student-page-wrapper">
    <!-- Header - Use StudentHeader for students -->
    <StudentHeader />

    <!-- ✅ Pending Post-Test Banner removed - now handled globally in App.vue -->

    <!-- Main Content -->
    <div
      class="max-w-7xl lg:w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8 pb-6 sm:pb-8 pt-32 sm:pt-36 md:pt-44"
    >
      <!-- Student Status Banner -->
      <StudentStatusBanner :dismissible="true" />

      <!-- Document Resubmission Alert -->
      <DocumentResubmissionAlert />

      <!-- Page Title -->
      <div class="mb-6 sm:mb-8 text-center px-2">
        <h1 class="text-2xl sm:text-3xl md:text-4xl font-extrabold text-gray-900">My Sessions</h1>
        <p class="text-sm sm:text-base md:text-lg text-black mt-2">
          Track your learning progress and generate AI-powered Study Materials.
        </p>
      </div>

      <!-- Summary Cards -->
      <div
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6 mb-6 sm:mb-8"
      >
        <SummaryCard
          icon="calendar"
          title="Upcoming Sessions"
          :value="totalUpcomingSessions.toString()"
          :isLoading="isLoadingSessions"
          color="blue"
        />
        <SummaryCard
          icon="check"
          title="Completed Sessions"
          :value="totalCompletedSessions.toString()"
          :isLoading="isLoadingSessions"
          color="green"
        />
        <SummaryCard
          icon="star"
          title="Your Average Rating"
          :value="averageRating"
          :isLoading="isLoadingSessions"
          color="yellow"
          :isRating="true"
        />
        <SummaryCard
          icon="clock"
          title="Total Hours"
          :value="totalHours"
          :isLoading="isLoadingSessions"
          color="purple"
        />
      </div>

      <!-- Session Navigation Tabs -->
      <SessionNavigation
        :active-tab="activeTab"
        :tabs="navigationTabs"
        @tab-change="handleTabChange"
      />

      <!-- Sessions Content -->
      <div v-if="activeTab === 'sessions'">
        <SessionManager
          :upcoming-sessions="upcomingSessions"
          :completed-sessions="[]"
          :total-completed-sessions="0"
          :hide-completed-sessions="true"
          @session-created="handleSessionCreated"
          @session-updated="handleSessionUpdated"
        />
      </div>

      <!-- Session History Tab -->
      <div v-else-if="activeTab === 'session-history'">
        <StudentSessionHistory
          :completed-sessions="completedSessions"
          :total-completed-sessions="totalCompletedSessions"
          :cancelled-sessions="cancelledSessions"
          :total-cancelled-sessions="totalCancelledSessions"
        />
      </div>
    </div>
  </div>

  <!-- Toast Container -->
  <ToastContainer />
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { db, auth } from "@/firebase";
import { collection, addDoc, updateDoc, doc } from "firebase/firestore";
import { sessionService } from "@/services/sessionService";
import type { SessionBookingData } from "@/types/availability";
import StudentHeader from "@/components/StudentHeader.vue";
import StudentStatusBanner from "@/components/Student/StudentStatusBanner.vue";
// ✅ PendingPostTestBanner import removed - now handled globally in App.vue
import DocumentResubmissionAlert from "@/components/Student/DocumentResubmissionAlert.vue";
import SummaryCard from "@/components/Student-Tutor/SummaryCard.vue";
import SessionManager from "@/components/Student-Tutor/SessionManager.vue";
import StudentSessionHistory from "@/components/Student/StudentSessionHistory.vue";
import SessionNavigation from "@/components/Student/StudentSessionNav.vue";
import { useSessionStore } from "@/composables/useSessionStore";
import FileReport from "@/components/FileReport.vue";
import ToastContainer from "@/components/ToastContainer.vue";
import { logError, logDebug } from "@/utils/secureLogger";
import { useToast } from "@/composables/useToast";

// Router for navigation
const router = useRouter();
const route = useRoute();

// Toast notifications
const { showWarning, showError } = useToast();

// Your Firebase user ID
const currentStudentId = ref("qb6ypxQovFZg9uzIyOTVlw2j35o1");

// Navigation state
const activeTab = ref("sessions");
const navigationTabs = ref([
  { id: "sessions", name: "Tutor Sessions" },
  { id: "session-history", name: "Session History" },
  { id: "study-materials", name: "AI-Generated Study Materials" },
]);

// Handle query parameter for initial tab
onMounted(() => {
  if (route.query.tab && typeof route.query.tab === "string") {
    const tabId = route.query.tab;
    if (["sessions", "session-history", "study-materials"].includes(tabId)) {
      activeTab.value = tabId;
    }
  }

  // ✅ Check if redirected due to pending post-test
  if (route.query.alert === "pending-post-test" && route.query.message) {
    showWarning(route.query.message as string);

    // Clear the query params from URL
    router.replace({ name: "student-sessions" });
  }
});

// Use shared session store
const {
  upcomingSessions,
  completedSessions,
  cancelledSessions,
  isLoadingSessions,
  totalCompletedSessions,
  totalUpcomingSessions,
  totalCancelledSessions,
  averageRating,
  totalHours,
} = useSessionStore();

// All session data is now managed by the shared store

// Navigation handler
const handleTabChange = (tabId: string) => {
  if (tabId === "study-materials") {
    router.push("/student/study-materials");
  } else {
    // Update active tab for sessions and session-history without query params
    activeTab.value = tabId;

    // Only add query param if it's not the default tab
    if (tabId !== "sessions") {
      router.replace({ path: "/student/sessions", query: { tab: tabId } });
    } else {
      // Clear query params for default tab
      router.replace("/student/sessions");
    }
  }
};

// Event handlers
const handleSessionCreated = async (sessionData: SessionBookingData | Record<string, unknown>) => {
  try {
    // Check if sessionData matches the expected format for sessionService.createSession
    if (sessionData.dateTime && sessionData.tutorId && sessionData.subject) {
      // Use the sessionService which includes rate limiting
      await sessionService.createSession(sessionData as SessionBookingData);
    } else {
      // Fallback for legacy format - add rate limiting manually
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("User must be authenticated to book a session");
      }

      // Check rate limiting before creating session (manual check)
      await sessionService.checkBookingRateLimit(currentUser.uid);

      // Create session with legacy direct Firebase approach
      await addDoc(collection(db, "sessions"), {
        ...sessionData,
        studentId: currentStudentId.value,
        createdAt: new Date(),
        status: "pending",
      });
    }
  } catch (error) {
    logError("Error creating session", error);

    // Handle rate limiting errors
    if (
      error instanceof Error &&
      (error.message.includes("Booking limit exceeded") ||
        error.message.includes("You can only book 3 sessions"))
    ) {
      logDebug("Showing rate limit warning toast");
      showWarning(error.message, 8000); // Show for 8 seconds
    } else if (
      error instanceof Error &&
      error.message.includes("You already have a session scheduled")
    ) {
      // Handle session overlap errors
      logDebug("Showing session overlap warning toast");
      showWarning(error.message, 8000); // Show for 8 seconds
    } else {
      logDebug("Showing generic error toast");
      showError("Failed to create session. Please try again or contact support.");
    }
  }
};

const handleSessionUpdated = async (sessionId: string, updateData: Record<string, unknown>) => {
  try {
    const sessionRef = doc(db, "sessions", sessionId);
    await updateDoc(sessionRef, {
      ...updateData,
      updatedAt: new Date(),
    });
  } catch (error) {
    logError("Error updating session", error);
  }
};
</script>

<style scoped>
.student-page-wrapper {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background: url("/student-reg-bg.png") center top/100% auto no-repeat;
  background-attachment: fixed;
  position: relative;
  width: 100%;
}
</style>
